import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Button, Typography, notification, Alert, InputNumber, Switch } from "antd";
import { useNavigate } from "react-router-dom";
import SubtenantApi from "../common/Apis/SubtenantApi";

const { Title } = Typography;

const UpdateSubTenantModal = ({ visible, onCancel, tenant }) => {
    const [form] = Form.useForm();
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(null);
    const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));

    // Populate form when modal opens with existing tenant data
    useEffect(() => {
        if (visible) {
            form.setFieldsValue({
                subTenantCode: tenant?.subTenantCode || "",
                subTenantName: tenant?.subTenantName || "",
                contactPerson: tenant?.contactPerson || "",
                contactEmail: tenant?.contactEmail || "",
                contactPhone: tenant?.contactPhone || "",
                contactAddress: tenant?.contactAddress || "",
                subTenantRate: tenant?.subTenantRate || null,
                enabled: tenant?.enabled !== undefined ? tenant.enabled : true, 
            });
            setIsButtonDisabled(true);
            setApiError(null);
        }
    }, [visible, tenant, form]);


    // Enable/disable Save button based on validation state
    const handleFieldsChange = () => {
        const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0);
        const values = form.getFieldsValue(["subTenantCode", "subTenantName"]);
        const allRequiredFilled = values.subTenantCode && values.subTenantName;
        setIsButtonDisabled(hasErrors || !allRequiredFilled);
    };

    const handleSave = () => {
        form
            .validateFields()
            .then((values) => {
                setApiError(null);
                const formData = JSON.stringify({
                    id: tenant?.id, 
                    tenantId: decodedToken?.tenantId,
                    subTenantCode: values?.subTenantCode,
                    subTenantName: values?.subTenantName,
                    contactPerson: values?.contactPerson || "",
                    contactEmail: values?.contactEmail || "",
                    contactPhone: values?.contactPhone || "",
                    contactAddress: values?.contactAddress || "",
                    subTenantRate: values?.subTenantRate || null,
                    enabled: values?.enabled !== undefined ? values.enabled : true, 
                    tenantDTO: {
                        id: decodedToken?.tenantId,
                    },
                });
                setLoading(true);
                SubtenantApi.PutSubtenant(tenant?.id, formData)
                    .then((response) => {
                        console.log(response);
                        notification.open({
                            message: <span style={{ fontWeight: "bold" }}>Store / Shop updated successfully!</span>,
                            closeIcon: false,
                            duration: 3,
                            style: { backgroundColor: "#b3ffb3", color: "green" },
                        });
                        onCancel();
                    })
                    .catch((err) => {
                        console.log(err);
                        if (err.apiError?.status) {
                            setApiError(err.apiError.message);
                            notification.open({
                                message: (
                                    <span style={{ fontWeight: "bold" }}>{err.apiError.status}</span>
                                ),
                                description: err.apiError.message,
                                closeIcon: false,
                                duration: 3,
                                style: { backgroundColor: "#fff2f0", color: "red" },
                            });
                        }
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            })
            .catch((info) => {
                console.log("Validate Failed:", info);
            });
    };

    return (
        <Modal
            open={visible}
            width={600}
            onCancel={onCancel}
            footer={null}
            maskClosable={false}
        >
            <Title level={4} className="text-center mb-3">
                Update store / shop
            </Title>

            <Form
                form={form}
                layout="vertical"
                name="update_sub_tenant_form"
                onFieldsChange={handleFieldsChange}
            >
                <Form.Item
                    label={
                        <span>
                            Store / Shop Code <span style={{ color: "red" }}>*</span>
                        </span>
                    }
                    name="subTenantCode"
                    rules={[
                        { required: true, message: "Please enter Store / Shop Code" },
                        {
                            pattern: /^[A-Za-z0-9]+$/,
                            message:
                                "Only letters and numbers are allowed. No spaces or special characters.",
                        },
                    ]}
                >
                    <Input placeholder="Enter Store / Shop Code" disabled={true}/>
                </Form.Item>

                <Form.Item
                    label={
                        <span>
                            Store / Shop Name <span style={{ color: "red" }}>*</span>
                        </span>
                    }
                    name="subTenantName"
                    rules={[{ required: true, message: "Please enter Store / Shop Name" }]}
                >
                    <Input placeholder="Enter Store / Shop Name" />
                </Form.Item>

                <Form.Item
                    label="Contact Person"
                    name="contactPerson"
                >
                    <Input placeholder="Enter Contact Person Name" />
                </Form.Item>

                <Form.Item
                    label="Contact Email"
                    name="contactEmail"
                    rules={[
                        {
                            type: 'email',
                            message: 'Please enter a valid email address',
                        },
                    ]}
                >
                    <Input placeholder="Enter Contact Email" />
                </Form.Item>

                <Form.Item
                    label="Contact Phone"
                    name="contactPhone"
                    rules={[
                        {
                            pattern: /^[0-9+\-\s()]+$/,
                            message: 'Please enter a valid phone number',
                        },
                    ]}
                >
                    <Input placeholder="Enter Contact Phone Number" />
                </Form.Item>

                <Form.Item
                    label="Contact Address"
                    name="contactAddress"
                >
                    <Input.TextArea 
                        placeholder="Enter Contact Address" 
                        rows={3}
                    />
                </Form.Item>

                <Form.Item
                    label="Store / Shop Rate"
                    name="subTenantRate"
                    rules={[
                        {
                            type: 'number',
                            min: 0,
                            message: 'Rate must be a positive number',
                        },
                    ]}
                >
                    <InputNumber
                        disabled
                        style={{ width: '100%' }}
                        placeholder="Enter Store / Shop Rate"
                        min={0}
                        step={0.01}
                        precision={2}
                        formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={value => value.replace(/\$\s?|(,*)/g, '')}
                    />
                </Form.Item>

                <Form.Item
                    label="Status"
                    name="enabled"
                    valuePropName="checked"
                >
                    <Switch 
                        checkedChildren="Enabled" 
                        unCheckedChildren="Disabled"
                        defaultChecked={tenant?.enabled !== undefined ? tenant.enabled : true}
                    />
                </Form.Item>

                {apiError && (
                    <Alert message={apiError} type="error" showIcon className="mb-3" />
                )}

                <Form.Item style={{ textAlign: "right" }}>
                    <Button onClick={onCancel} style={{ marginRight: 8 }}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleSave}
                        disabled={isButtonDisabled}
                        loading={loading}
                    >
                        Update
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default UpdateSubTenantModal;